import { Route, Routes, useLocation } from 'react-router';
import _ from 'lodash';
import axios from 'axios';

import 'material-symbols';
import 'material-symbols/outlined.css';

import '@fontsource/open-sans';
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/500.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/open-sans/800.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/bitter/600.css';
import '@fontsource/bitter/400.css';

import './App.scss';

// get Content + config
import Content from './content';

// Helper

// Pages
import Login from './pages/login/index';

import { deactivateServices } from './_helper/deactivateServices';
import browserInfo from './_helper/browserInfo';
import { Revalidation } from './comp/revalidation/revalidation';
import ErrorBoundary from './_helper/ErrorBoundary';
import ScrollToTop from './_helper/ScrollToTop';
import LiveCredentialInfoLayer from './_helper/liveCredentialInfoLayer';
import FetchNewData from './_helper/fetchNewData';
import ProtectedRoute from '@/comp/protectedRoute/protectedRoute';
import Logout from './pages/logout';
import SeatReservation from './pages/extra-services/seat-reservation';
import Excursion from './pages/extra-services/excursion';
import ExcursionDetails from './pages/extra-services/excursion/details';
import ExcursionRates from './pages/extra-services/excursion/rates';
import Insurance from './pages/extra-services/insurance';
import Transfer from './pages/extra-services/transfer';
import TransferCruise from './pages/extra-services/transfer-cruise';
import RailAndFly from './pages/extra-services/railAndFly';
import Cancellation from './pages/customerarea/cancellation';
import PartialCancellation from './pages/customerarea/cancellation/partial/index';
import PartialCancellationOverview from './pages/customerarea/cancellation/partial/overview';
import Booking from './pages/booking';
import BookingSuccess from './pages/booking-success';
import BookingFailed from './pages/booking/bookingFailed';
import Payment from './pages/customerarea/payment';
import PaymentRefund from './pages/customerarea/payment-refund/index';
import Claim from './pages/customerarea/claim';
import DigitalTourGuide from './pages/customerarea/digital-tour-guide';
import EmergencyContact from './pages/customerarea/emergency-contact';
import Apis from './pages/customerarea/apis';
import NotFound from './pages/notFound';
import ConsentBanner from './comp/consentBanner';
import PrivacyPolicy from './pages/privacy-policy';
import OnSpot from './pages/customerarea/onSpot';
import { AppProvider } from './context/AppContext';
import { isExtrasKey } from '@/_data/contentExtras';
import { isInternalIp } from '@/_helper/dynamicTestBooking';
import { DebugMenu } from '@/comp/debugMenu/debugMenu';
import { Index } from './pages/index';
import { ManageBooking } from './pages/index/views/manage-booking';
import HomeLayout from '@/layout/HomeLayout';
import { ShowDocuments } from './pages/index/views/show-documents';
import { ExtraServicesOptions } from './pages/index/views/extra-service-options';
import { PaymentStatus } from './pages/index/views/payment-status';
import { CancellationOptions } from './pages/index/views/cancellation-options';
import { ClaimOption } from './pages/index/views/claim-options';
import { HelpOnspot } from './pages/index/views/help-on-spot';

/* temporary user authorization for staging */
if (process.env.VITE_ENV === 'staging') {
    axios.defaults.headers.common['Authorization'] = 'Basic ' + btoa('service_team:7MevCzxhX9yTW9Hd');
}

// global vars
window.$deactivateLogin = Boolean(process.env.VITE_DEACTIVATE_LOGIN === 'true');
window.$affiliate = process.env.VITE_AFFILIATE ?? false;
window.$affiliateServices = process.env.VITE_AFFILIATE_ACTIVATE_SERVICES;

window.$baseUrl = process.env.VITE_BASE_URL;
window.$website = process.env.VITE_WEBSITE;
window.$requestUrl = process.env.VITE_REQUEST_URL;
window.$serviceUrl = process.env.VITE_SERVICE_URL;
window.$tourGuideRegistrationUrl = process.env.VITE_TOUR_GUIDE_REGISTRATION_URL;
window.$testLoginAllowed = process.env.VITE_TEST_LOGIN_ALLOWED;
window.$3DS2Allowed = process.env.VITE_3DS2_ALLOWED;
window.$revalidateEnabled = Boolean(process.env.VITE_REVALIDATE_ENABLED === 'true');
window.$allowFlightChangeSearch = process.env.VITE_ALLOW_FLIGHT_CHANGE_SEARCH;
window.$allowHotelChangeSearch = process.env.VITE_ALLOW_HOTEL_CHANGE_SEARCH;
window.$allowEmergencyContact = process.env.VITE_ALLOW_EMERGENCY_CONTACT;
window.$bookingCancelled = false;
window.$searchFrontendUrl = process.env.VITE_SEARCH_FRONTEND_URL;
window.$claimEnabled = process.env.VITE_CLAIM_ENABLED;
window.$claimUploadSize = process.env.VITE_CLAIM_UPLOAD_SIZE;
window.$requestData = {};
window.$browserInfo = browserInfo();

window.$cookieName = 'CookieConsentCustomerArea';
window.$content = Content;

// deactivate extras
window.$deactivateExtras = [];
if (process.env.VITE_DEACTIVATE_EXTRAS !== undefined) {
    window.$deactivateExtras = JSON.parse(process.env.VITE_DEACTIVATE_EXTRAS);

    let deactivateExtras = window.$deactivateExtras;
    _.forEach(deactivateExtras, function (v: string) {
        if (isExtrasKey(v) && window.$content.extras[v].active !== undefined) {
            window.$content.extras[v].active = false;
        }
    });
}

// deactivate services
window.deactivateServices = [];
deactivateServices();

if (process.env.VITE_ENV === 'production') {
    delete window.$content.fakeLogin;
}

window.$documentTitle = ' | ' + process.env.VITE_SEO_TITLE;

window.$autoLogin = false;

// set devicetype : mobile | desktop
if (/Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent)) {
    sessionStorage.setItem('devicetype', 'mobile');
} else {
    sessionStorage.setItem('devicetype', 'desktop');
}

// affiliate
if (window.$affiliate) {
    switch (window.$affiliate) {
        case 'ch24':
            window.$documentTitle = ' | CHECK24 Kundenbereich bei AurumTours';
            break;
        default:
            window.$documentTitle = ' | Aurumtours';
    }

    document.querySelector('html')?.classList.add('affiliate-' + window.$affiliate);
}

const App = () => {
    const location = useLocation();
    const isInternal = isInternalIp();

    return (
        <ErrorBoundary location={location}>
            <ScrollToTop />
            <LiveCredentialInfoLayer />
            {!window.$affiliate && <ConsentBanner />}

            {/** Debug Menu */}
            {isInternal && (
                <ProtectedRoute>
                    <DebugMenu />
                </ProtectedRoute>
            )}

            <Routes>
                <Route path="/deeplink/:target/:hash?/:mode?" element={<Login location={location} />} />

                <Route path="/login" element={<Login location={location} />} />
                <Route path="/logout" element={<Logout />} />

                {!window.$affiliate && (
                    <Route
                        path="/datenschutz"
                        element={
                            <AppProvider isAffiliate={window.$affiliate === 'ch24'}>
                                <PrivacyPolicy />
                            </AppProvider>
                        }
                    />
                )}

                <Route element={<ProtectedRoute />}>
                    <Route element={<HomeLayout />}>
                        <Route path="/:index?" element={<Index />} />

                        <Route path="/buchung-verwalten" element={<ManageBooking />} />
                        <Route path="/dokumente-anzeigen" element={<ShowDocuments />} />
                        <Route path="/zusatzleistung" element={<ExtraServicesOptions />} />
                        <Route path="/zahlungsstatus" element={<PaymentStatus />} />
                        <Route path="/stornierungsoptionen" element={<CancellationOptions />} />
                        <Route path="/reise-reklamieren" element={<ClaimOption />} />
                        <Route path="/reise-hilfe-vor-ort" element={<HelpOnspot />} />
                    </Route>

                    <Route path="/revalidierung" element={<Revalidation />} />
                    <Route path="/sitzplatzreservierung" element={<SeatReservation />} />
                    <Route path="/ausfluege" element={<Excursion />} />
                    <Route path="/ausfluege/details/:id" element={<ExcursionDetails />} />
                    <Route path="/ausfluege/rates/:id" element={<ExcursionRates />} />
                    <Route path="/versicherung" element={<Insurance />} />
                    <Route path="/transfer" element={<Transfer />} />
                    <Route path="/transfer-kreuzfahrt" element={<TransferCruise />} />
                    <Route path="/zug-zum-flug" element={<RailAndFly />} />
                    <Route path="/stornierung" element={<Cancellation />} />
                    <Route path="/leistung-stornieren/:service?" element={<PartialCancellation />} />
                    <Route
                        path="/leistung-stornieren/:service/uebersicht/:index"
                        element={<PartialCancellationOverview />}
                    />
                    <Route path="/buchung/:travelType?" element={<Booking />} />
                    <Route path="/uebersicht" element={<BookingSuccess />} />
                    <Route path="/buchung-fehlgeschlagen" element={<BookingFailed />} />
                    <Route path="/zahlung" element={<Payment />} />
                    <Route path="/rueckzahlung" element={<PaymentRefund type="refund" />} />
                    <Route path="/gutschrift" element={<PaymentRefund type="credit" />} />
                    <Route path="/reklamation" element={<Claim />} />
                    <Route path="/hilfe-vor-ort/:target?" element={<OnSpot />} />
                    <Route path="/digitale-reiseleitung" element={<DigitalTourGuide />} />
                    <Route path="/notfall-kontakt" element={<EmergencyContact />} />
                    <Route path="/apis" element={<Apis />} />
                </Route>

                <Route path="*" element={<ProtectedRoute>{<NotFound />}</ProtectedRoute>} />
            </Routes>
            <FetchNewData location={location} />
        </ErrorBoundary>
    );
};
export default App;
